// Deleting a channel or a space (18-channel-settings.md, "Delete", "Deleting a channel or a space";
// 94-services-api.md): the room's admins only, where the company has the admin service, never a
// direct chat. The admin service plans (a space), deletes and reports progress; members see the
// room go as a deletion when it carried chat.desyr.deleting.
import { RoomEvent, type MatrixClient, type Room } from 'matrix-js-sdk';
import { useEffect, useState } from 'react';
import { Avatar } from '../blocks/Bits';
import { Button } from '../blocks/Button';
import { Dialog, DialogButtons, ErrorLine } from '../blocks/Dialog';
import { MenuItem } from '../blocks/Menu';
import { toast } from '../blocks/Toast';
import { LoadState, useLoad } from '../blocks/Wait';
import { adminWords } from '../account/words';
import { navigate } from '../core/router';
import { t } from '../i18n';
import { adminBase, chatAdmin } from '../session/admin';
import { thumbnail } from '../session/media';
import { isDirect, refFor } from '../session/rooms';
import { dropRooms, stillJoined } from '../session/gone';
import { chatIcons } from '../chat/icons';
import { Card, Section, goBehind } from './frame';
import { closeRoomSettings } from './RoomSettings';
import { powerOf } from './roles';

interface Named {
  roomId: string;
  name: string;
}
interface Plan {
  delete: Named[];
  stay: Named[];
  subspaces: Named[];
}
interface Progress {
  status: 'running' | 'done' | 'failed';
  rooms: (Named & { status: string; code?: string })[];
}

/** Rooms this device deleted itself: their own toast, not the members' one. */
const deletedHere = new Set<string>();

/** Leaves the open page for Home when it shows one of these rooms (behind the settings window: it stays). */
const awayFrom = (client: MatrixClient, ids: string[], keep = false) =>
  ids.some((id) => location.pathname.includes(encodeURIComponent(refFor(client.getRoom(id) ?? ({ roomId: id } as Room))))) &&
  (keep ? goBehind('/home/') : navigate('/home/', { replace: true }));

/** The room's admins where the circle has the admin service, never a direct chat (18, Delete). */
export const mayDelete = (client: MatrixClient, room: Room) => !!adminBase(client) && !isDirect(client, room.roomId) && room.getMyMembership() === 'join' && powerOf(room, client.getUserId()!) >= 100;

let openDelete: ((room: Room) => void) | undefined;
/** The delete dialog, from the settings' Delete card or straight from a menu (07, 08, 11, 18). */
export function DeleteHost({ client }: { client: MatrixClient }) {
  const [room, setRoom] = useState<Room>();
  useEffect(() => ((openDelete = setRoom), () => void (openDelete = undefined)), []);
  return room ? <DeleteDialog client={client} room={room} onClose={() => setRoom(undefined)} /> : null;
}

/** "Delete channel…" / "Delete space…" in the menus: the dialog at once, never the settings first. */
export const DeleteItem = ({ client, room }: { client: MatrixClient; room: Room }) =>
  mayDelete(client, room) ? (
    <MenuItem icon={chatIcons.delete} destructive onSelect={() => openDelete?.(room)}>
      <span data-desyr-delete-menu={room.isSpaceRoom() ? 'space' : 'channel'}>{t(room.isSpaceRoom() ? 'delete.menuSpace' : 'delete.menuChannel')}</span>
    </MenuItem>
  ) : null;

export function DeleteSection({ client, room }: { client: MatrixClient; room: Room }) {
  if (!mayDelete(client, room)) return null;
  const space = room.isSpaceRoom();
  return (
    <Section title={t('delete.section')}>
      <Card
        hook={space ? 'delete-space' : 'delete-channel'}
        icon={chatIcons.delete}
        title={<span data-desyr-delete={space ? 'space' : 'channel'}>{t(space ? 'delete.space' : 'delete.channel')}</span>}
        text={t(space ? 'delete.spaceLine' : 'delete.channelLine')}
        control={<Button small variant="critical-soft" onClick={() => openDelete?.(room)}>{t('delete.button')}</Button>}
      />
    </Section>
  );
}
function DeleteDialog({ client, room, onClose }: { client: MatrixClient; room: Room; onClose: () => void }) {
  const space = room.isSpaceRoom();
  const path = `/rooms/${encodeURIComponent(room.roomId)}`;
  // Already gone (18, 4b): "not there" or "not admin" and no longer joined on the server: dropped, with its toast.
  const gone = async (e: unknown) => {
    const code = (e as { code?: string }).code;
    if ((code !== 'desyr.request.not_found' && code !== 'desyr.rooms.not_admin') || (await stillJoined(client, room.roomId).catch(() => true))) throw e;
    onClose();
    closeRoomSettings();
    toast(t(space ? 'gone.space' : 'gone.channel'));
    dropRooms(client, [room.roomId]);
  };
  const [plan, retry] = useLoad(() => (space ? chatAdmin<Plan>(client, `${path}/delete-plan`).catch(gone) : Promise.resolve(undefined)), [room.roomId]);
  const [busy, setBusy] = useState(false);
  const [slow, setSlow] = useState(false);
  const [failure, setFailure] = useState<string>();
  const words = (e: unknown) =>
    adminWords(e, 'save', {
      'desyr.rooms.not_admin': t(space ? 'delete.notAdminSpace' : 'delete.notAdminChannel'),
      'desyr.rooms.direct': t('delete.direct'),
      'desyr.rooms.deleting': t('delete.already'),
    });

  const run = async () => {
    setBusy(true);
    setFailure(undefined);
    const slowly = setTimeout(() => setSlow(true), 10_000);
    try {
      const started = await chatAdmin<{ deletion: string; rooms: Named[] }>(client, `${path}/delete`, 'POST').catch(gone);
      if (!started) return;
      const { deletion, rooms } = started;
      rooms.forEach((r) => deletedHere.add(r.roomId)); // their leave is this device's own deletion
      let progress: Progress;
      do {
        await new Promise((r) => setTimeout(r, 1000));
        progress = await chatAdmin<Progress>(client, `/rooms/deletions/${encodeURIComponent(deletion)}`);
      } while (progress.status === 'running');
      const done = progress.rooms.filter((r) => r.status === 'done');
      awayFrom(client, done.map((r) => r.roomId));
      dropRooms(client, done.map((r) => r.roomId)); // at once, so they never come back (03, rule 4)
      if (progress.status === 'failed') {
        const failed = progress.rooms.find((r) => r.status === 'failed');
        return setFailure(done.length ? t('delete.partly') : t('delete.failed', { reason: words({ code: failed?.code }) }));
      }
      const channels = progress.rooms.length - 1;
      toast(channels > 0 ? t('delete.doneMany', { name: room.name, n: channels }) : t('delete.done', { name: room.name }));
      closeRoomSettings(); // and the dialog (18, 4)
      onClose();
    } catch (e) {
      setFailure(t('delete.failed', { reason: words(e) }));
    } finally {
      clearTimeout(slowly);
      setBusy(false);
      setSlow(false);
    }
  };

  const list = (key: 'delete.goes' | 'delete.stays' | 'delete.subspaces', rooms: Named[] = []) =>
    rooms.length > 0 && (
      <>
        <p className="dialog-text">{t(key)}</p>
        <ul className="delete-list">
          {rooms.map((r) => (
            <li key={r.roomId}>
              <Avatar id={r.roomId} name={r.name} src={thumbnail(client, client.getRoom(r.roomId)?.getMxcAvatarUrl(), 24)} size={24} round={false} />
              {r.name}
            </li>
          ))}
        </ul>
      </>
    );
  const goes = plan.state === 'done' ? plan.value?.delete.filter((r) => r.roomId !== room.roomId) : undefined;
  return (
    <Dialog
      title={room.name ? t('delete.titleNamed', { name: room.name }) : t(space ? 'delete.titleSpace' : 'delete.titleChannel')}
      alert
      required={busy}
      className="narrow"
      onClose={onClose}
    >
      <div data-desyr-delete-dialog="">
        <p className="dialog-text">{t('delete.text')}</p>
        {space && <LoadState status={plan} retry={retry} compact />}
        {list('delete.goes', goes)}
        {plan.state === 'done' && list('delete.stays', plan.value?.stay)}
        {plan.state === 'done' && list('delete.subspaces', plan.value?.subspaces)}
        {slow && <p className="dialog-text">{t('delete.slow')}</p>}
        <ErrorLine>{failure}</ErrorLine>
      </div>
      <DialogButtons>
        <Button variant="text" disabled={busy} onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button variant="critical-text" disabled={busy || (space && plan.state !== 'done')} busy={busy} onClick={() => void run()}>
          {t(busy ? 'delete.busy' : 'delete.confirm')}
        </Button>
      </DialogButtons>
    </Dialog>
  );
}

/** For everyone else: a room that carried chat.desyr.deleting and is left reads as deleted (18, 6). */
export function watchDeletions(client: MatrixClient): () => void {
  const onLeft = (room: Room, membership: string) => {
    if (membership !== 'leave') return;
    // The deleter's own rooms go one by one: the page behind goes Home at once, the dialog stays (18, 3).
    if (deletedHere.has(room.roomId)) return void awayFrom(client, [room.roomId], true);
    if (!room.currentState.getStateEvents('chat.desyr.deleting', '')) return;
    toast(t('delete.done', { name: room.name }));
    awayFrom(client, [room.roomId]);
  };
  client.on(RoomEvent.MyMembership, onLeft);
  return () => void client.off(RoomEvent.MyMembership, onLeft);
}
